import { Avatar, Button } from '@ohear/ui'
/**
 * Profile-photo uploader.
 *
 * Pure HTML <input type="file"> + a circular preview — no crop library
 * (per spec). The user picks a JPG/PNG ≤ 2 MB; we read it via FileReader
 * for the preview, then call the parent-supplied `onUpload(file)` which
 * is responsible for: requesting a presigned URL, PUT-ing to R2, and
 * committing the resulting r2_key via `PATCH /me`.
 *
 * Errors are surfaced via local state and rendered inline (not via toast)
 * so the user sees the explanation directly under the file picker.
 */
import { type ChangeEvent, useRef, useState } from 'react'

const MAX_BYTES = 2 * 1024 * 1024
const ALLOWED_TYPES = new Set(['image/jpeg', 'image/png'])

export interface ImageUploadProps {
  /** Current signed URL for the existing profile image (or null). */
  currentUrl?: string | null
  /** Display name used as Avatar fallback initial. */
  displayName?: string
  /** Called with the chosen file after local validation. The parent owns
   *  the actual upload (presigned URL fetch + R2 PUT + commit). */
  onUpload: (file: File) => Promise<void>
  /** Optional remove handler — shown only when `currentUrl` is set. */
  onRemove?: () => Promise<void>
  disabled?: boolean
}

export function ImageUpload(props: ImageUploadProps) {
  const { currentUrl, displayName, onUpload, onRemove, disabled } = props
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [busy, setBusy] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const reset = () => {
    if (inputRef.current) inputRef.current.value = ''
    setPreviewUrl(null)
  }

  const handleFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setError(null)

    if (!ALLOWED_TYPES.has(file.type)) {
      setError('Photos work best as JPG or PNG.')
      reset()
      return
    }
    if (file.size > MAX_BYTES) {
      setError("That's a bit big — please pick an image under 2 MB.")
      reset()
      return
    }

    // Local preview while the upload is in flight.
    const reader = new FileReader()
    reader.onload = () => {
      if (typeof reader.result === 'string') setPreviewUrl(reader.result)
    }
    reader.readAsDataURL(file)

    setBusy(true)
    try {
      await onUpload(file)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed. Please try again.')
      reset()
    } finally {
      setBusy(false)
    }
  }

  const shownUrl = previewUrl ?? currentUrl ?? undefined

  return (
    <div className="flex items-center gap-4">
      <Avatar src={shownUrl} name={displayName ?? '?'} size="lg" />
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            type="button"
            disabled={disabled || busy}
            loading={busy}
            onClick={() => inputRef.current?.click()}
          >
            {currentUrl ? 'Change photo' : 'Upload photo'}
          </Button>
          {currentUrl && onRemove ? (
            <Button
              variant="ghost"
              type="button"
              disabled={disabled || busy}
              onClick={() => {
                setBusy(true)
                onRemove()
                  .catch((e) => setError(e instanceof Error ? e.message : 'Could not remove photo'))
                  .finally(() => setBusy(false))
              }}
            >
              Remove
            </Button>
          ) : null}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png"
          className="hidden"
          onChange={(e) => void handleFile(e)}
          disabled={disabled || busy}
        />
        <p className="text-[12px] text-[var(--text-2)] m-0">JPG or PNG, up to 2 MB.</p>
        {error ? (
          <p role="alert" className="text-[12px] text-[var(--danger-text,#a8071a)] m-0">
            {error}
          </p>
        ) : null}
      </div>
    </div>
  )
}
